// src/auth/faskesSession.js

/**
 * Helpers session khusus portal FASKES.
 * - Simpan token & user di sessionStorage (key: faskes.session)
 * - Role yang diizinkan: faskes, faskes_operator, faskes_admin
 * - Resolve API base URL dari env / fallback + domain baru (lebih robust)
 * - Menyediakan login/logout & probe status server
 */

export const ALLOWED_FASKES_ROLES = [
  "faskes",
  "faskes_operator",
  "faskes_admin",
];

// ===== BE & FE baru
const PROD_API_HOST_HTTP = "http://apiklik.bkkpangkalpinang.com";
const PROD_API_HOST_HTTPS = "https://apiklik.bkkpangkalpinang.com";
const PROD_FE_HOST = "https://klik.bkkpangkalpinang.com";

// cache base yang sudah proven agar request berikutnya lebih cepat
const BASE_CACHE_KEY = "kbkk.faskes.api.base";
const BASE_CACHE_TTL_MS = 10 * 60 * 1000; // 10 menit

/* =====================================
   Sesi
===================================== */
const KEY = "faskes.session";

export function saveFaskesSession({ token, user }) {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ token: String(token || ""), user: user || null })
    );
  } catch {}
}

export function clearFaskesSession() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
}

export function getFaskesSession() {
  try {
    return (
      JSON.parse(sessionStorage.getItem(KEY) || "null") || {
        token: "",
        user: null,
      }
    );
  } catch {
    return { token: "", user: null };
  }
}

/* =====================================
   Role helpers
===================================== */
export function extractRole(user) {
  if (!user) return "";
  if (user.role && typeof user.role === "object" && user.role.slug)
    return String(user.role.slug);
  if (typeof user.role === "string") return user.role;
  return "";
}

export function isAllowedFaskesRole(slug) {
  if (!slug) return false;
  const s = String(slug).toLowerCase();
  return ALLOWED_FASKES_ROLES.includes(s);
}

/* =====================================
   Base URL helpers
===================================== */
function stripSlash(s) {
  return String(s || "").trim().replace(/\/+$/, "");
}

function normalizeBase(s) {
  if (!s) return "";
  let x = stripSlash(s);
  if (!/\/api$/i.test(x)) x = `${x}/api`;
  return x;
}

function readBaseCached() {
  try {
    const raw = sessionStorage.getItem(BASE_CACHE_KEY);
    if (!raw) return "";
    const { ts, base } = JSON.parse(raw);
    if (!base || typeof base !== "string") return "";
    if (Date.now() - ts > BASE_CACHE_TTL_MS) return "";
    return base;
  } catch {
    return "";
  }
}

function writeBaseCached(base) {
  try {
    sessionStorage.setItem(
      BASE_CACHE_KEY,
      JSON.stringify({ ts: Date.now(), base })
    );
  } catch {}
}

function candidatesFromLocation() {
  if (typeof window === "undefined") {
    return [
      `${PROD_API_HOST_HTTPS}/api`,
      `${PROD_API_HOST_HTTP}/api`,
      `${PROD_API_HOST_HTTPS}/public/api`,
      `${PROD_API_HOST_HTTP}/public/api`,
      "http://127.0.0.1:8000/api",
      "http://localhost:8000/api",
    ];
  }

  const { protocol, hostname, origin, port } = window.location;
  const isDevPort = ["3000", "5173", "4173"].includes(port || "");

  const list = [];

  // ✅ prioritas produksi ke BE baru (HTTPS dulu)
  list.push(`${PROD_API_HOST_HTTPS}/api`);
  list.push(`${PROD_API_HOST_HTTP}/api`);
  list.push(`${PROD_API_HOST_HTTPS}/public/api`);
  list.push(`${PROD_API_HOST_HTTP}/public/api`);

  if (isDevPort) {
    // typical laravel:8000 + react:3000
    list.push(`${protocol}//${hostname}:8000/api`);
    list.push("http://127.0.0.1:8000/api");
    list.push("http://localhost:8000/api");
  } else {
    // fallback: backend & frontend di host yg sama
    list.push(`${origin}/api`);
    list.push(`${origin}/public/api`);

    // subfolder shared hosting (kalau masih dipakai)
    list.push(`${origin}/klik-bkk-api/public/api`);
    list.push(`${origin}/bkk-api/public/api`);

    // jika FE domain baru, tambahkan ulang BE baru agar tetap prioritas
    if (stripSlash(origin) === stripSlash(PROD_FE_HOST)) {
      list.push(`${PROD_API_HOST_HTTPS}/api`);
      list.push(`${PROD_API_HOST_HTTP}/api`);
      list.push(`${PROD_API_HOST_HTTPS}/public/api`);
      list.push(`${PROD_API_HOST_HTTP}/public/api`);
    }
  }

  return list;
}

/** Base URL utama untuk ditampilkan (string saja, tidak Promise) */
export function getApiBaseForDisplay() {
  const envCRA = (process.env.REACT_APP_API_BASE_URL || "").trim();
  const envViteCompat = (process.env.VITE_API_BASE_URL || "").trim();
  const env = envCRA || envViteCompat;
  if (env) return normalizeBase(env);

  const cached = readBaseCached();
  if (cached) return cached;

  const [first] = candidatesFromLocation();
  return normalizeBase(first);
}

/** Kandidat base untuk dicoba saat request (anti salah port) */
export function makeApiCandidates() {
  const list = [];

  const cached = readBaseCached();
  if (cached) list.push(cached);

  const envCRA = (process.env.REACT_APP_API_BASE_URL || "").trim();
  const envViteCompat = (process.env.VITE_API_BASE_URL || "").trim();
  const env = envCRA || envViteCompat;
  if (env) list.push(normalizeBase(env));

  list.push(...candidatesFromLocation().map(normalizeBase));

  return Array.from(new Set(list.filter(Boolean)));
}

async function pingBase(apiBase, timeoutMs = 3500) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${stripSlash(apiBase)}/ping`, {
      method: "GET",
      headers: { Accept: "application/json" },
      mode: "cors",
      credentials: "omit",
      signal: ctrl.signal,
    });
    return !!res && res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

async function resolveApiBase() {
  const cached = readBaseCached();
  if (cached) return cached;

  const cands = makeApiCandidates();
  for (const b of cands) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await pingBase(b);
    if (ok) {
      writeBaseCached(b);
      return b;
    }
  }

  const fallback = cands[0] || normalizeBase(PROD_API_HOST_HTTPS);
  writeBaseCached(fallback);
  return fallback;
}

/* =====================================
   Auth API (login/logout) & status
===================================== */

/**
 * Login Faskes
 * - Mencoba ke beberapa kandidat base URL hingga sukses.
 * - Mengembalikan { base, data } bila sukses, dan menyimpan sesi.
 * - Melempar Error dengan .status (401/403/422/500) bila gagal.
 */
export async function faskesLogin(email, password) {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const rawPassword = String(password || "");

  if (!/^[a-z0-9][a-z0-9._+-]*@[a-z0-9.-]+\.[a-z]{2,63}$/i.test(normalizedEmail)) {
    const err = new Error("Format email tidak valid.");
    err.status = 422;
    throw err;
  }

  if (!rawPassword || rawPassword.length > 255) {
    const err = new Error("Password wajib diisi.");
    err.status = 422;
    throw err;
  }

  const payload = { email: normalizedEmail, password: rawPassword };
  const isLocal = typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname || "");
  const trustedBases = isLocal
    ? makeApiCandidates()
    : [normalizeBase(PROD_API_HOST_HTTPS)];

  clearFaskesSession();

  let lastErr = new Error("Network error");
  for (const base of trustedBases) {
    try {
      const res = await fetch(`${base}/auth/login-faskes`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "omit",
        mode: "cors",
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body?.message || "Login gagal");
        err.status = res.status;
        // Fail closed: jangan mencoba server lain setelah backend merespons.
        throw err;
      }

      const token = String(body?.token || body?.access_token || "").trim();
      if (!token || !body?.user || typeof body.user !== "object") {
        const err = new Error("Respons autentikasi tidak lengkap.");
        err.status = 502;
        throw err;
      }

      saveFaskesSession({ token, user: body.user });
      writeBaseCached(base);
      return { base, data: { ...body, token } };
    } catch (e) {
      lastErr = e;
      if (e?.status) break; // HTTP response: berhenti, jangan fallback.
    }
  }
  throw lastErr;
}

/**
 * Logout Faskes
 * - Selalu menghapus sesi lokal dulu
 * - Mencoba revoke token di backend (silent failure OK)
 */
export async function faskesLogout() {
  const { token } = getFaskesSession();
  clearFaskesSession();

  if (!token) return;

  // coba base yang proven dulu
  const baseFirst = await resolveApiBase().catch(() => "");
  const ordered = baseFirst
    ? [baseFirst, ...makeApiCandidates().filter((b) => b !== baseFirst)]
    : makeApiCandidates();

  for (const base of ordered) {
    try {
      await fetch(`${base}/auth/logout`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        mode: "cors",
        credentials: "omit",
      });
      writeBaseCached(base);
      break;
    } catch {
      // silent; lanjut kandidat berikutnya
    }
  }
}

/**
 * Probe status server untuk menyalakan badge "Terhubung / Tidak Terhubung"
 * - Endpoint publik & ringan: GET /roles
 * - Return { ok, base } → pilih base pertama yang merespons 200
 */
export async function probeApi() {
  // coba base proven dulu
  const baseFirst = await resolveApiBase().catch(() => "");
  const ordered = baseFirst
    ? [baseFirst, ...makeApiCandidates().filter((b) => b !== baseFirst)]
    : makeApiCandidates();

  for (const base of ordered) {
    try {
      const res = await fetch(`${base.replace(/\/+$/, "")}/roles`, {
        method: "GET",
        headers: { Accept: "application/json" },
        mode: "cors",
        credentials: "omit",
      });

      if (res.ok) {
        writeBaseCached(base);
        return { ok: true, base };
      }
    } catch {
      // coba kandidat berikutnya
    }
  }
  return { ok: false, base: "" };
}

/* =====================================
   Misc helpers untuk UI
===================================== */
export function getCurrentRole() {
  const { user } = getFaskesSession();
  return extractRole(user);
}

export function isFaskesLoggedIn() {
  const { token, user } = getFaskesSession();
  return !!token && isAllowedFaskesRole(extractRole(user));
}

export function authHeader() {
  const { token } = getFaskesSession();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
